import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MainRatesService, RateMode } from '../../services/main-rates.service';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import Swal from 'sweetalert2';
import { mensajeSinPermiso } from './franjas-editor.rules';

export interface VesUsdtModalData {
  tasaVigente: {
    id: number;
    promedioBinance: number;
    comisionPct: number;
    tasaFinal: number;
    responsible: string;
    createdAt: string;
    modo?: RateMode;
  } | null;
  datosEnVivo: {
    promedioBinanceActual: number;
    comisionConfigurada: number;
    tasaFinalCalculada: number;
    bancos: Record<string, { valor: number; activo: boolean }>;
  };
}

@Component({
  selector: 'app-edit-ves-usdt-modal',
  template: `
    <div style="padding: 0;">
      <!-- Header naranja -->
      <div style="background: linear-gradient(135deg, #fff7ed, #ffedd5); padding: 20px 24px; border-bottom: 2px solid #fed7aa;">
        <div style="display: flex; align-items: center; gap: 10px;">
          <mat-icon style="color: #ea580c; font-size: 28px; width: 28px; height: 28px;">swap_vert</mat-icon>
          <h1 style="font-size: 1.25rem; font-weight: 700; color: #9a3412; margin: 0;">Configurar Tasa VES &rarr; USDT</h1>
        </div>
      </div>

      <div style="padding: 20px 24px;">
        <!-- Datos en vivo -->
        <div style="background: #f9fafb; border-radius: 12px; padding: 14px; margin-bottom: 14px;">
          <p style="font-size: 0.8rem; font-weight: 700; color: #374151; margin-bottom: 8px;">Datos en Vivo</p>
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div style="display: flex; align-items: center; gap: 6px;">
              <span style="font-size: 0.8rem; color: #6b7280;">Promedio Binance:</span>
              <span style="font-size: 0.85rem; font-weight: 700; color: #374151;">{{ data.datosEnVivo.promedioBinanceActual | number:'1.2-2':'es-CL' }} VES</span>
            </div>
            <div style="display: flex; align-items: center; gap: 6px;">
              <span style="font-size: 0.8rem; color: #6b7280;">Comisi&oacute;n actual:</span>
              <span style="font-size: 0.85rem; font-weight: 700; color: #ea580c;">+{{ data.datosEnVivo.comisionConfigurada | number:'1.0-2':'es-CL' }} %</span>
            </div>
          </div>
        </div>

        <!-- Selector de modo -->
        <div style="display: flex; gap: 8px; margin-bottom: 14px;">
          <button type="button" (click)="setModo('AUTOMATICO_PCT')"
            [style.background]="!isManual ? '#ea580c' : 'white'"
            [style.color]="!isManual ? 'white' : '#374151'"
            [style.borderColor]="!isManual ? '#ea580c' : '#d1d5db'"
            style="flex: 1; padding: 9px 12px; border: 1px solid; border-radius: 10px; font-weight: 700; font-size: 0.8rem; cursor: pointer; transition: all 0.2s ease;">
            Autom&aacute;tico por %
          </button>
          <button type="button" (click)="setModo('MANUAL_FIJO')"
            [style.background]="isManual ? '#ea580c' : 'white'"
            [style.color]="isManual ? 'white' : '#374151'"
            [style.borderColor]="isManual ? '#ea580c' : '#d1d5db'"
            style="flex: 1; padding: 9px 12px; border: 1px solid; border-radius: 10px; font-weight: 700; font-size: 0.8rem; cursor: pointer; transition: all 0.2s ease;">
            Precio manual fijo
          </button>
        </div>

        <!-- Bancos activos (solo relevante en modo automático) -->
        <div *ngIf="!isManual" style="background: #fff7ed; border: 1px solid #fed7aa; border-radius: 12px; padding: 12px; margin-bottom: 14px;">
          <p style="font-size: 0.75rem; font-weight: 600; color: #ea580c; margin-bottom: 6px;">Bancos incluidos en promedio:</p>
          <div class="flex flex-wrap gap-2">
            <span *ngFor="let banco of getBancosActivos()"
              style="display: inline-flex; padding: 3px 10px; background: #f0fdf4; border: 1px solid #bbf7d0; border-radius: 20px; font-size: 0.7rem; font-weight: 600; color: #1C593D;">
              {{ banco }}
            </span>
          </div>
        </div>

        <!-- Formulario -->
        <form [formGroup]="form">
          <!-- Modo AUTOMÁTICO: comisión -->
          <div *ngIf="!isManual" style="margin-bottom: 14px;">
            <label style="display: block; font-size: 0.8rem; font-weight: 600; color: #374151; margin-bottom: 6px;">Comisi&oacute;n / Recargo (%)</label>
            <input type="number" formControlName="comisionPct"
              style="width: 100%; padding: 10px 14px; border: 1px solid #d1d5db; border-radius: 10px; font-size: 0.9rem; outline: none; transition: border-color 0.2s ease;"
              onfocus="this.style.borderColor='#ea580c'; this.style.borderWidth='2px'"
              onblur="this.style.borderColor='#d1d5db'; this.style.borderWidth='1px'"
              step="0.01" min="0" max="100" placeholder="Ej: 3.00">
            <p *ngIf="form.get('comisionPct')?.invalid && form.get('comisionPct')?.touched"
              style="font-size: 0.75rem; color: #dc2626; font-weight: 600; margin-top: 4px;">
              {{ getErrorMessage('comisionPct') }}
            </p>
          </div>

          <!-- Modo MANUAL: precio fijo -->
          <div *ngIf="isManual" style="margin-bottom: 14px;">
            <label style="display: block; font-size: 0.8rem; font-weight: 600; color: #374151; margin-bottom: 6px;">Precio manual fijo &mdash; 1 USDT = ? VES</label>
            <input type="number" formControlName="tasaFija"
              style="width: 100%; padding: 10px 14px; border: 1px solid #d1d5db; border-radius: 10px; font-size: 0.9rem; outline: none; transition: border-color 0.2s ease;"
              onfocus="this.style.borderColor='#ea580c'; this.style.borderWidth='2px'"
              onblur="this.style.borderColor='#d1d5db'; this.style.borderWidth='1px'"
              step="0.01" min="0.01" placeholder="Ej: 829">
            <p style="font-size: 0.72rem; color: #92400e; margin-top: 4px;">
              El sistema ignora la variaci&oacute;n del promedio Binance y mantiene este precio fijo.
            </p>
            <p *ngIf="form.get('tasaFija')?.invalid && form.get('tasaFija')?.touched"
              style="font-size: 0.75rem; color: #dc2626; font-weight: 600; margin-top: 4px;">
              {{ getErrorMessage('tasaFija') }}
            </p>
          </div>

          <!-- Preview en tiempo real -->
          <div *ngIf="previewData" style="background: linear-gradient(135deg, #fff7ed, #ffedd5); border: 2px solid #fed7aa; border-radius: 14px; padding: 16px; margin-bottom: 14px;">
            <p style="font-size: 0.8rem; font-weight: 700; color: #9a3412; margin-bottom: 8px;">Vista Previa</p>
            <p style="font-size: 1.6rem; font-weight: 800; color: #9a3412; text-align: center; margin-bottom: 4px;">
              1 USDT = {{ previewData.tasaFinal | number:'1.2-2':'es-CL' }} VES
            </p>
            <p *ngIf="!isManual" style="font-size: 0.75rem; color: #6b7280; text-align: center; margin-bottom: 10px;">
              {{ previewData.promedioBinance | number:'1.2-2':'es-CL' }} x (1 + {{ previewData.comisionPct | number:'1.0-2':'es-CL' }} %)
            </p>
            <p *ngIf="isManual" style="font-size: 0.75rem; color: #92400e; text-align: center; margin-bottom: 10px; font-weight: 700;">
              Precio fijo configurado
            </p>
            <div class="grid grid-cols-3 gap-2">
              <div *ngFor="let ejemplo of previewData.ejemplos"
                style="background: white; border: 1px solid #e5e7eb; border-radius: 8px; padding: 8px; text-align: center;">
                <p style="font-size: 0.65rem; color: #6b7280;">{{ ejemplo.ves }} VES</p>
                <p style="font-size: 0.85rem; font-weight: 800; color: #9a3412;">{{ ejemplo.usdt | number:'1.4-4':'es-CL' }} USDT</p>
              </div>
            </div>
          </div>

          <!-- Loading preview -->
          <div *ngIf="loadingPreview" class="flex flex-col items-center py-4">
            <mat-spinner diameter="30"></mat-spinner>
            <p style="font-size: 0.8rem; color: #6b7280; margin-top: 6px;">Calculando...</p>
          </div>
        </form>

        <!-- Modo que quedará activo al guardar -->
        <p style="font-size: 0.78rem; text-align: center; color: #374151; margin-bottom: 10px;">
          Modo activo:
          <strong style="color: #9a3412;">{{ isManual ? 'Precio manual fijo' : 'Automático por comisión %' }}</strong>
        </p>

        <!-- Botones (en el celular quedan fijos abajo: .modal-botones en styles.sass) -->
        <div class="flex flex-col sm:flex-row gap-2 justify-center mt-2 modal-botones">
          <button type="button" (click)="onCancel()"
            class="w-full sm:w-auto"
            style="display: inline-flex; align-items: center; justify-content: center; gap: 6px; padding: 8px 24px; background: white; color: #374151; border: 1px solid #d1d5db; border-radius: 10px; font-weight: 600; font-size: 0.85rem; cursor: pointer; transition: all 0.2s ease;"
            onmouseover="this.style.background='#f3f4f6'" onmouseout="this.style.background='white'">
            Cancelar
          </button>
          <button type="button" (click)="onSave()"
            [disabled]="form.invalid || saving"
            [style.opacity]="(form.invalid || saving) ? '0.5' : '1'"
            [style.cursor]="(form.invalid || saving) ? 'not-allowed' : 'pointer'"
            class="w-full sm:w-auto"
            style="display: inline-flex; align-items: center; justify-content: center; gap: 8px; padding: 10px 36px; background: #ea580c; color: white; border: none; border-radius: 10px; font-weight: 700; font-size: 0.9rem; cursor: pointer; transition: background 0.2s ease;"
            onmouseover="this.style.background='#c2410c'" onmouseout="this.style.background='#ea580c'">
            <mat-icon style="font-size: 18px; width: 18px; height: 18px; color: white;">save</mat-icon>
            {{ saving ? 'Guardando...' : 'Guardar Configuración' }}
          </button>
        </div>
      </div>
    </div>
  `,
  styles: []
})
export class EditVesUsdtModalComponent implements OnInit {
  form: FormGroup;
  previewData: any = null;
  loadingPreview = false;
  saving = false;

  constructor(
    private fb: FormBuilder,
    public dialogRef: MatDialogRef<EditVesUsdtModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: VesUsdtModalData,
    private mainRatesService: MainRatesService
  ) {
    const modoInicial: RateMode =
      data.tasaVigente?.modo === 'MANUAL_FIJO' ? 'MANUAL_FIJO' : 'AUTOMATICO_PCT';

    this.form = this.fb.group({
      modo: [modoInicial],
      comisionPct: [
        data.datosEnVivo.comisionConfigurada || 0,
        [Validators.min(0), Validators.max(100)]
      ],
      tasaFija: [
        modoInicial === 'MANUAL_FIJO' ? data.tasaVigente?.tasaFinal ?? null : null,
        [Validators.min(0.01)]
      ]
    });
  }

  get isManual(): boolean {
    return this.form.get('modo')?.value === 'MANUAL_FIJO';
  }

  ngOnInit(): void {
    this.applyValidatorsForMode();
    this.refreshPreview();

    // Comisión (modo automático) → preview del backend
    this.form.get('comisionPct')?.valueChanges.pipe(
      debounceTime(500),
      distinctUntilChanged()
    ).subscribe((value) => {
      if (!this.isManual && value >= 0 && value <= 100) {
        this.loadPreviewAutomatico(value);
      }
    });

    // Precio fijo (modo manual) → preview local
    this.form.get('tasaFija')?.valueChanges.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(() => {
      if (this.isManual) {
        this.buildManualPreview();
      }
    });
  }

  /**
   * Cambia el modo, ajusta validadores y refresca la vista previa.
   */
  setModo(modo: RateMode): void {
    if (this.form.get('modo')?.value === modo) return;
    this.form.get('modo')?.setValue(modo);
    this.applyValidatorsForMode();
    this.refreshPreview();
  }

  /**
   * Aplica los validadores según el modo activo (no combinar required + condicional
   * para replicar la validación condicional del backend).
   */
  private applyValidatorsForMode(): void {
    const comision = this.form.get('comisionPct');
    const tasaFija = this.form.get('tasaFija');

    if (this.isManual) {
      comision?.clearValidators();
      tasaFija?.setValidators([Validators.required, Validators.min(0.01)]);
    } else {
      tasaFija?.clearValidators();
      comision?.setValidators([
        Validators.required,
        Validators.min(0),
        Validators.max(100)
      ]);
    }
    comision?.updateValueAndValidity({ emitEvent: false });
    tasaFija?.updateValueAndValidity({ emitEvent: false });
  }

  /** Refresca la vista previa según el modo actual. */
  private refreshPreview(): void {
    if (this.isManual) {
      this.buildManualPreview();
    } else {
      this.loadPreviewAutomatico(this.form.get('comisionPct')?.value);
    }
  }

  /**
   * Carga el preview de la tasa VES→USDT desde el backend (modo automático).
   */
  loadPreviewAutomatico(comisionPct: number): void {
    this.loadingPreview = true;
    this.mainRatesService.previewVesUsdtRate(comisionPct).subscribe({
      next: (response) => {
        if (response.success) {
          this.previewData = response.data;
        }
        this.loadingPreview = false;
      },
      error: () => {
        this.loadingPreview = false;
      }
    });
  }

  /**
   * Construye la vista previa local para el modo manual fijo.
   * VES→USDT: usdt = ves / tasaFija.
   */
  private buildManualPreview(): void {
    const tasaFija = Number(this.form.get('tasaFija')?.value);
    if (!Number.isFinite(tasaFija) || tasaFija <= 0) {
      this.previewData = null;
      return;
    }
    this.previewData = {
      tasaFinal: tasaFija,
      promedioBinance: this.data.datosEnVivo.promedioBinanceActual,
      comisionPct: 0,
      ejemplos: [500, 1000, 5000].map((ves) => ({
        ves,
        usdt: Math.round((ves / tasaFija) * 10000) / 10000
      }))
    };
  }

  /**
   * Obtiene los bancos activos
   */
  getBancosActivos(): string[] {
    if (!this.data.datosEnVivo.bancos) return [];
    return Object.entries(this.data.datosEnVivo.bancos)
      .filter(([_, info]) => info.activo)
      .map(([nombre]) => nombre);
  }

  /**
   * Guarda la configuración según el modo activo.
   */
  onSave(): void {
    if (this.form.invalid) return;
    this.saving = true;

    const modo: RateMode = this.form.get('modo')?.value;
    const payload =
      modo === 'MANUAL_FIJO'
        ? { modo, tasaFija: Number(this.form.get('tasaFija')?.value) }
        : { modo, comisionPct: Number(this.form.get('comisionPct')?.value) };

    this.mainRatesService.guardarVesUsdtConfig(payload).subscribe({
      next: (response) => {
        this.saving = false;
        if (response.success) {
          this.dialogRef.close(response.data);
        }
      },
      error: (err) => {
        this.saving = false;
        // Prod: 403 si el correo no está en la lista de editores de tasas.
        const sinPermiso = mensajeSinPermiso(err);
        if (sinPermiso) {
          Swal.fire({
            icon: 'warning',
            title: 'Acción no disponible',
            text: sinPermiso,
            confirmButtonText: 'Entendido',
            confirmButtonColor: '#f59e0b',
          });
        }
      }
    });
  }

  /**
   * Cancela y cierra el modal
   */
  onCancel(): void {
    this.dialogRef.close();
  }

  /**
   * Obtiene mensaje de error
   */
  getErrorMessage(fieldName: string): string {
    const field = this.form.get(fieldName);

    if (field?.hasError('required')) {
      return fieldName === 'tasaFija'
        ? 'El precio manual es requerido'
        : 'La comisión es requerida';
    }
    if (field?.hasError('min')) {
      return fieldName === 'tasaFija'
        ? 'El precio debe ser mayor a 0'
        : 'La comisión mínima es 0%';
    }
    if (field?.hasError('max')) {
      return 'La comisión máxima es 100%';
    }
    return '';
  }
}
